import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { customFocusApi } from '../../api/customFocus';
import { FOCUS_TEMPLATES, focusTemplatesForAltitude } from '../../utils/focusTemplates';
import CreateCustomFocusModal from './CreateCustomFocusModal';
import './CustomFocus.css';

const BUILT_IN_TEMPLATES = FOCUS_TEMPLATES;

/**
 * CustomFocusItem - Single custom focus in the list
 */
function CustomFocusItem({ focus, isSelected, onSelect }) {
  // Try to get the icon component
  const iconName = focus.icon ? focus.icon.charAt(0).toUpperCase() + focus.icon.slice(1) : 'Pencil';
  const IconComponent = LucideIcons[iconName] || LucideIcons.Pencil;

  return (
    <button
      className={`focus-picker-item custom-focus-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(focus)}
    >
      <span className="focus-picker-icon">
        <IconComponent size={18} />
      </span>
      <span className="focus-picker-content">
        <span className="focus-picker-label">{focus.name}</span>
        <span className="focus-picker-desc">
          {focus.description || `${focus.exploration_mode} mode`}
        </span>
        {focus.scope === 'project' && (
          <span className="focus-picker-scope">Project-wide</span>
        )}
      </span>
    </button>
  );
}

/**
 * BuiltInTemplateItem - Single built-in template in the list
 */
function BuiltInTemplateItem({ template, isSelected, onSelect }) {
  return (
    <button
      className={`focus-picker-item builtin-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(template.key)}
    >
      <span className="focus-picker-icon emoji">{template.emoji}</span>
      <span className="focus-picker-content">
        <span className="focus-picker-label">{template.label}</span>
        <span className="focus-picker-desc">{template.description}</span>
      </span>
    </button>
  );
}

/**
 * FocusPicker - Main component for selecting focus mode target
 *
 * Shows built-in templates and custom focuses, allows creating new custom focuses.
 *
 * Props:
 * - storyId: Story UUID (required)
 * - projectId: Project UUID (for scope options)
 * - selectedTemplate: Currently selected built-in template key
 * - selectedCustomFocus: Currently selected custom focus object
 * - onSelectTemplate: Callback when built-in template is selected
 * - onSelectCustomFocus: Callback when custom focus is selected
 */
export default function FocusPicker({
  storyId,
  projectId,
  selectedTemplate,
  selectedCustomFocus,
  onSelectTemplate,
  onSelectCustomFocus,
  seriesAltitude = false,
}) {
  const [customFocuses, setCustomFocuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const builtIn = focusTemplatesForAltitude(seriesAltitude).filter((t) => t.key !== 'custom');

  useEffect(() => {
    const loadCustomFocuses = async () => {
      if (!storyId && !projectId) {
        setLoading(false);
        return;
      }

      try {
        const response = storyId
          ? await customFocusApi.listForStory(storyId)
          : await customFocusApi.listForProject(projectId);
        setCustomFocuses(response.focuses || []);
      } catch (err) {
        console.error('Failed to load custom focuses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCustomFocuses();
  }, [storyId, projectId]);

  const handleCreateFocus = (newFocus) => {
    setCustomFocuses((prev) => [...prev, newFocus]);
    // Auto-select the new focus
    onSelectCustomFocus(newFocus);
  };

  const handleSelectBuiltIn = (templateKey) => {
    onSelectTemplate(templateKey);
    onSelectCustomFocus(null);
  };

  const handleSelectCustom = (focus) => {
    onSelectCustomFocus(focus);
    onSelectTemplate(null);
  };

  // Sort custom focuses: story-level first, then project-level
  const sortedFocuses = [...customFocuses].sort((a, b) => {
    if (a.scope === 'story' && b.scope === 'project') return -1;
    if (a.scope === 'project' && b.scope === 'story') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="focus-picker">
      <div className="focus-picker-section">
        <h4 className="focus-picker-section-title">Built-in Templates</h4>
        <div className="focus-picker-list">
          {builtIn.map((template) => (
            <BuiltInTemplateItem
              key={template.key}
              template={template}
              isSelected={selectedTemplate === template.key && !selectedCustomFocus}
              onSelect={handleSelectBuiltIn}
            />
          ))}
        </div>
      </div>

      <div className="focus-picker-section">
        <h4 className="focus-picker-section-title">
          Custom Focuses
          <button
            className="focus-picker-create-btn"
            onClick={() => setShowCreateModal(true)}
            title="Create custom focus"
          >
            <LucideIcons.Plus size={16} />
          </button>
        </h4>

        {loading ? (
          <div className="focus-picker-loading">Loading...</div>
        ) : error ? (
          <div className="focus-picker-error">{error}</div>
        ) : sortedFocuses.length === 0 ? (
          <div className="focus-picker-empty">
            <p>No custom focuses yet.</p>
            <button
              className="focus-picker-empty-create"
              onClick={() => setShowCreateModal(true)}
            >
              Create your first focus
            </button>
          </div>
        ) : (
          <div className="focus-picker-list">
            {sortedFocuses.map((focus) => (
              <CustomFocusItem
                key={focus.id}
                focus={focus}
                isSelected={selectedCustomFocus?.id === focus.id}
                onSelect={handleSelectCustom}
              />
            ))}
          </div>
        )}
      </div>

      <CreateCustomFocusModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreateFocus}
        storyId={storyId}
        projectId={projectId}
      />
    </div>
  );
}

export { FocusPicker, BUILT_IN_TEMPLATES };
